import { useState, useRef } from 'react';
import { useJournal } from '../hooks/useJournal';
import { useBadges } from '../hooks/useBadges';

// ── Avatars (mesmos do perfil) ────────────────────────────────
const EMOJIS_AVATAR = [
  '👩🏻', '👩🏼', '👩🏽', '👩‍🦱', '👩🏽‍🦱', '👱‍♀️', '👩‍🦰', '👩‍🦳', '🧕🏽', '👩🏾',
  '👨', '👨🏻', '👨🏽', '👨‍🦱',
  '🧘‍♀️', '🧘🏾‍♀️', '🧘‍♂️', '🏊‍♀️', '🏃‍♀️', '🚴‍♀️',
  '🌿', '🌸', '🌺', '🌙', '🌱', '🦋', '🌊', '☀️',
];

// ── Slides de introdução ─────────────────────────────────────
const SLIDES = [
  {
    gradient: 'linear-gradient(145deg, #2f2b5f 0%, #7b5ea7 100%)',
    icon: (
      <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor" style={{ color: 'rgba(255,255,255,0.9)' }}>
        <path d="M12 2 L13.8 10.2 L22 12 L13.8 13.8 L12 22 L10.2 13.8 L2 12 L10.2 10.2 Z"/>
      </svg>
    ),
    titulo: 'Bem-vinda(o) ao\nClube do Eu',
    subtitulo: 'Um espaço para crescer, se conectar e construir a melhor versão de si mesma(o) — no seu próprio ritmo.',
  },
  {
    gradient: 'linear-gradient(145deg, #4a7ba7 0%, #6fae8f 100%)',
    icon: (
      <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.9)" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/>
      </svg>
    ),
    titulo: 'Seu momento\ndiário',
    subtitulo: 'Uma reflexão, um sentimento, uma palavra. Pequenos gestos diários que criam grandes transformações ao longo do tempo.',
  },
  {
    gradient: 'linear-gradient(145deg, #c4873d 0%, #e8a55a 100%)',
    icon: (
      <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.9)" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="8" r="6"/><path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11"/>
      </svg>
    ),
    titulo: 'Construa\nhábitos reais',
    subtitulo: 'Sequências, badges, desafios e metas. Cada dia que você aparece é uma conquista — e o clube celebra isso com você.',
  },
  {
    gradient: 'linear-gradient(145deg, #7b5ea7 0%, #c8a8d8 100%)',
    icon: (
      <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.9)" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
      </svg>
    ),
    titulo: 'Você não\ncaminha sozinha(o)',
    subtitulo: 'Reflexões compartilhadas, desafios coletivos e uma comunidade que se apoia. Aqui, crescer é algo que se faz junto.',
  },
];

// ── Componente de dots ───────────────────────────────────────
function Dots({ total, atual }) {
  return (
    <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginTop: 28 }}>
      {Array.from({ length: total }, (_, i) => (
        <div key={i} style={{
          height: 6, borderRadius: 3,
          width: i === atual ? 20 : 6,
          background: i === atual ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.3)',
          transition: 'all .3s ease',
        }} />
      ))}
    </div>
  );
}

// ── Slide de introdução ──────────────────────────────────────
function IntroSlide({ slide, index, total, onNext, onSkip }) {
  const isLast = index === total - 1;
  const touchStartX = useRef(null);

  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (diff > 40) onNext();
    touchStartX.current = null;
  };

  return (
    <div
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      style={{
        minHeight: '100vh', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        background: slide.gradient,
        padding: '48px 32px 40px',
        boxSizing: 'border-box',
      }}
    >
      {/* Skip */}
      <button
        onClick={onSkip}
        style={{
          position: 'absolute', top: 20, right: 24,
          background: 'none', border: 'none', cursor: 'pointer',
          fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 400,
          color: 'rgba(255,255,255,0.6)', padding: '4px 8px',
        }}
      >
        Pular
      </button>

      {/* Ícone */}
      <div style={{ marginBottom: 36 }}>{slide.icon}</div>

      {/* Texto */}
      <div style={{ textAlign: 'center', flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', maxWidth: 320 }}>
        <h1 style={{
          fontFamily: "'Cormorant Garamond', Georgia, serif",
          fontSize: 38, fontWeight: 500, color: '#fff',
          lineHeight: 1.15, marginBottom: 16, whiteSpace: 'pre-line',
        }}>
          {slide.titulo}
        </h1>
        <p style={{
          fontFamily: "'DM Sans', sans-serif",
          fontSize: 16, fontWeight: 300, color: 'rgba(255,255,255,0.8)',
          lineHeight: 1.7,
        }}>
          {slide.subtitulo}
        </p>
      </div>

      {/* Dots */}
      <Dots total={total} atual={index} />

      {/* Botão */}
      <button
        onClick={onNext}
        style={{
          marginTop: 36, width: '100%', maxWidth: 320,
          padding: '15px 24px', borderRadius: 14, border: 'none',
          background: 'rgba(255,255,255,0.18)',
          backdropFilter: 'blur(8px)',
          fontFamily: "'DM Sans', sans-serif", fontSize: 16, fontWeight: 600,
          color: '#fff', cursor: 'pointer', letterSpacing: '0.02em',
          transition: 'background .2s',
        }}
        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.28)'}
        onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.18)'}
      >
        {isLast ? 'Criar meu perfil →' : 'Próximo →'}
      </button>
    </div>
  );
}

// ── Passo de perfil ──────────────────────────────────────────
function ProfileStep({ name, setName, avatar, setAvatar, onNext, saving }) {
  const [mostrarGrid, setMostrarGrid] = useState(false);

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      padding: '48px 24px 40px', boxSizing: 'border-box',
      background: 'radial-gradient(ellipse 70% 60% at 20% 20%, rgba(123,94,167,0.1) 0%, transparent 65%), var(--bg)',
    }}>
      <div style={{ width: '100%', maxWidth: 400 }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ marginBottom: 16 }}>
            <svg width="36" height="36" viewBox="0 0 24 24" fill="var(--primary)">
              <path d="M12 2 L13.8 10.2 L22 12 L13.8 13.8 L12 22 L10.2 13.8 L2 12 L10.2 10.2 Z"/>
            </svg>
          </div>
          <h1 style={{
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            fontSize: 34, fontWeight: 500, color: 'var(--navy)',
            lineHeight: 1.2, marginBottom: 10,
          }}>
            Como quer<br />ser chamada(o)?
          </h1>
          <p style={{
            fontFamily: "'DM Sans', sans-serif", fontSize: 15,
            fontWeight: 300, color: 'var(--muted)', lineHeight: 1.6,
          }}>
            Escolha um avatar e um nome de exibição.
          </p>
        </div>

        {/* Avatar circle + toggle */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 24 }}>
          <div style={{
            width: 80, height: 80, borderRadius: '50%',
            background: 'var(--primary)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 38, marginBottom: 10,
          }}>
            {avatar}
          </div>
          <button
            onClick={() => setMostrarGrid(v => !v)}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontFamily: "'DM Sans', sans-serif", fontSize: 14,
              color: 'var(--primary)', fontWeight: 500,
              textDecoration: 'underline', textUnderlineOffset: 3,
            }}
          >
            {mostrarGrid ? 'Fechar' : 'Mudar avatar'}
          </button>
        </div>

        {/* Grid de avatars */}
        {mostrarGrid && (
          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: 8,
            justifyContent: 'center', marginBottom: 24,
            padding: '16px', background: 'var(--surface)',
            borderRadius: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
          }}>
            {EMOJIS_AVATAR.map(a => (
              <button
                key={a}
                onClick={() => { setAvatar(a); setMostrarGrid(false); }}
                style={{
                  fontSize: 26, width: 44, height: 44, borderRadius: 10,
                  background: avatar === a ? 'var(--lavender-light, #f0eef8)' : 'transparent',
                  border: avatar === a ? '2px solid var(--lavender)' : '2px solid transparent',
                  cursor: 'pointer', lineHeight: 1, transition: 'all .15s',
                }}
              >
                {a}
              </button>
            ))}
          </div>
        )}

        {/* Label + Nome */}
        <p style={{
          fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 500,
          letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--muted)',
          marginBottom: 8,
        }}>
          Nome de exibição
        </p>
        <input
          className="input"
          placeholder="Seu nome ou apelido"
          value={name}
          onChange={e => setName(e.target.value)}
          maxLength={40}
          style={{ marginBottom: 20, fontSize: 16 }}
          autoFocus
        />

        <button
          className="btn btn-primary"
          style={{ width: '100%', padding: '15px', fontSize: 16 }}
          onClick={onNext}
          disabled={saving || !name.trim()}
        >
          {saving ? 'Salvando...' : 'Esse sou eu →'}
        </button>
      </div>
    </div>
  );
}

// ── Passo de primeiro check-in ───────────────────────────────
const SENTIMENTOS = [
  { label: 'Ótima', score: 5, emoji: '🌟' },
  { label: 'Bem',   score: 4, emoji: '😊' },
  { label: 'Ok',    score: 3, emoji: '😐' },
  { label: 'Difícil', score: 2, emoji: '😔' },
  { label: 'Pesado', score: 1, emoji: '🌧️' },
];

function CheckInStep({ onSubmit, saving, nome, avatar }) {
  const [selecionado, setSelecionado] = useState(null);
  const [nota, setNota] = useState('');

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      padding: '48px 24px 40px', boxSizing: 'border-box',
      background: 'radial-gradient(ellipse 70% 60% at 80% 80%, rgba(111,174,143,0.1) 0%, transparent 65%), var(--bg)',
    }}>
      <div style={{ width: '100%', maxWidth: 400 }}>
        {/* Avatar + saudação */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>{avatar}</div>
          <h1 style={{
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            fontSize: 32, fontWeight: 500, color: 'var(--navy)',
            lineHeight: 1.2, marginBottom: 10,
          }}>
            Seu primeiro momento,<br />{nome}
          </h1>
          <p style={{
            fontFamily: "'DM Sans', sans-serif", fontSize: 15,
            fontWeight: 300, color: 'var(--muted)', lineHeight: 1.6,
          }}>
            Como você está chegando ao Clube do Eu hoje?
          </p>
        </div>

        {/* Sentimentos */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 24 }}>
          {SENTIMENTOS.map(s => (
            <button
              key={s.score}
              onClick={() => setSelecionado(s)}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                padding: '12px 14px', borderRadius: 12, border: 'none', cursor: 'pointer',
                background: selecionado?.score === s.score ? 'var(--lavender-light, #f0eef8)' : 'var(--surface)',
                outline: selecionado?.score === s.score ? '2px solid var(--lavender)' : '2px solid transparent',
                transition: 'all .15s', minWidth: 60,
              }}
            >
              <span style={{ fontSize: 24 }}>{s.emoji}</span>
              <span style={{
                fontFamily: "'DM Sans', sans-serif", fontSize: 12, fontWeight: 500,
                color: selecionado?.score === s.score ? 'var(--primary)' : 'var(--muted)',
              }}>
                {s.label}
              </span>
            </button>
          ))}
        </div>

        {/* Texto livre */}
        <textarea
          className="input"
          placeholder="Compartilhe como está se sentindo, o que está esperando, o que te trouxe até aqui..."
          value={nota}
          onChange={e => setNota(e.target.value)}
          rows={4}
          style={{ marginBottom: 16, fontSize: 15, lineHeight: 1.6, resize: 'none' }}
        />

        <button
          className="btn btn-primary"
          style={{ width: '100%', padding: '15px', fontSize: 16, marginBottom: 12 }}
          onClick={() => onSubmit(selecionado, nota)}
          disabled={saving || !selecionado}
        >
          {saving ? 'Registrando...' : 'Entrar no Clube do Eu ✨'}
        </button>

        <button
          style={{
            width: '100%', background: 'none', border: 'none', cursor: 'pointer',
            fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: 'var(--muted)',
            padding: '8px', textDecoration: 'underline', textUnderlineOffset: 3,
          }}
          onClick={() => onSubmit(null, '')}
        >
          Pular por agora
        </button>
      </div>
    </div>
  );
}

// ── Componente principal ─────────────────────────────────────
export default function OnboardingPage({ user, profile, onSave, onComplete }) {
  const [fase, setFase] = useState('slides'); // 'slides' | 'perfil' | 'checkin'
  const [slideIndex, setSlideIndex] = useState(0);
  const [name, setName] = useState(profile?.display_name || '');
  const [avatar, setAvatar] = useState(profile?.avatar_emoji || '🌱');
  const [saving, setSaving] = useState(false);
  const [showToast, setShowToast] = useState(false);

  const { checkIn, saveSentimento, saveNote } = useJournal(user.id);
  const { evaluateBadges } = useBadges(user.id);

  const handleNextSlide = () => {
    if (slideIndex < SLIDES.length - 1) {
      setSlideIndex(i => i + 1);
    } else {
      setFase('perfil');
    }
  };

  const handleSkipSlides = () => setFase('perfil');

  const handleProfileSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    await onSave({ display_name: name.trim(), avatar_emoji: avatar });
    if (window.fbq) window.fbq('track', 'Lead');
    setSaving(false);
    onComplete();
  };

  const handleCheckIn = async (sentimento, nota) => {
    setSaving(true);
    if (sentimento) {
      await checkIn(sentimento.score);
      await saveSentimento(sentimento.label, sentimento.score);
    }
    if (nota?.trim()) await saveNote(nota.trim());
    await evaluateBadges([{ completed: true, note: nota || '', mood_score: sentimento?.score || null }]);
    setSaving(false);
    setShowToast(true);
    if (window.fbq) window.fbq('track', 'Lead');
    setTimeout(() => onComplete(), 1800);
  };

  return (
    <>
      {fase === 'slides' && (
        <IntroSlide
          slide={SLIDES[slideIndex]}
          index={slideIndex}
          total={SLIDES.length}
          onNext={handleNextSlide}
          onSkip={handleSkipSlides}
        />
      )}

      {fase === 'perfil' && (
        <ProfileStep
          name={name}
          setName={setName}
          avatar={avatar}
          setAvatar={setAvatar}
          onNext={handleProfileSave}
          saving={saving}
        />
      )}

      {fase === 'checkin' && (
        <CheckInStep
          nome={name.trim()}
          avatar={avatar}
          onSubmit={handleCheckIn}
          saving={saving}
        />
      )}

      {showToast && (
        <div style={{
          position: 'fixed', bottom: 40, left: '50%', transform: 'translateX(-50%)',
          background: 'linear-gradient(135deg, var(--primary), var(--lavender))',
          color: '#fff', padding: '14px 28px', borderRadius: 50,
          fontFamily: "'DM Sans', sans-serif", fontSize: 15, fontWeight: 600,
          boxShadow: '0 8px 32px rgba(123,94,167,0.4)', zIndex: 9999,
          whiteSpace: 'nowrap',
        }}>
          ✨ Bem-vinda(o) ao Clube do Eu!
        </div>
      )}
    </>
  );
}
